import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Target, Award, Briefcase } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const TimeshareOPC = () => {
  const qualifications = [
    "Must be outgoing and assertive",
    "Must be able to handle cash and tickets accurately and securely",
    "Enjoy working with tourists",
    "Positive, Self Motivated, and able to work in a team environment",
    "Excellent face-to-face customer service skills",
    "Has the ability to overcome objections",
    "Requires a flexible work schedule including mornings, nights, weekends, and holidays",
    "Passionate about genuinely helping clients get a better deal"
  ];

  const responsibilities = [
    "Persuade potential clients to visit resorts by maximizing their savings on area theme parks and attractions",
    "Engage with guests/clients to facilitate the auditing process and pre-qualify candidates for resort tours",
    "Provide 5-Star customer service to increase resort tour sign-up rates and ticket sales",
    "Utilize a consultative approach when offering various discounted ticket sales & resort room bookings",
    "Open and close POS ticket sales terminals and balance cash and credit card transactions",
    "Maintain a professional appearance and work environment at all times",
    "Stay up to date on ticket prices and special promotions"
  ];

  return (
    <div data-testid="timeshare-opc-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1.5 bg-white/10 text-white text-xs font-bold uppercase tracking-wider rounded-full mb-6">
              Primary Service
            </span>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-6">
              Timeshare OPC Marketing Teams
            </h1>
            <p className="text-xl text-slate-300 mb-4">
              OFF PROPERTY CONTACT (OPC) TIMESHARE MARKETING
            </p>
            <p className="text-lg text-slate-400 mb-8">
              Resorts want tours, We get them tours. Our expert OPC marketers drive 
              qualified leads to your property through strategic engagement.
            </p>
            <Link to="/partner">
              <Button 
                size="lg" 
                className="bg-[#EA580C] hover:bg-[#c2410c] text-white px-8"
                data-testid="opc-hero-cta"
              >
                Transform Your OPC Strategy
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
                Our OPC Marketers
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Expert Vacation Marketing Professionals
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Our team excels at encouraging vacationers to visit resorts by offering them 
                extremely low rates on discounted theme parks and attraction tickets. Our marketers 
                must be proficient in and required to book room nights, sell attraction tickets, 
                and provide concierge services for visitors and guests that are on vacation.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: <Target />, label: "Tour Generation" },
                  { icon: <Users />, label: "Guest Engagement" },
                  { icon: <Award />, label: "5-Star Service" },
                  { icon: <Briefcase />, label: "Ticket Sales" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-10 h-10 bg-[#1E3A8A] text-white rounded-lg flex items-center justify-center">
                      {item.icon}
                    </div>
                    <span className="font-medium text-slate-700">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Key Responsibilities</h3>
              <ul className="space-y-4">
                {responsibilities.slice(0, 5).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#0D9488] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Our Approach
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              These Are a Few of Our Favorite Things
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Maximize Guest Savings",
                desc: "Persuade potential clients to visit resorts by maximizing their savings on area theme parks and attractions."
              },
              {
                title: "Pre-Qualify Candidates",
                desc: "Engage with guests to facilitate the auditing process and pre-qualify candidates for resort tours."
              },
              {
                title: "5-Star Customer Service",
                desc: "Provide exceptional service to increase resort tour sign-up rates, ticket sales, and room bookings."
              },
              {
                title: "Consultative Selling",
                desc: "Utilize a consultative approach when offering discounted tickets and resort bookings to optimize experiences."
              },
              {
                title: "Professional Operations",
                desc: "Maintain professional appearance and manage POS terminals with accurate cash handling."
              },
              {
                title: "Stay Current",
                desc: "Keep up to date on ticket prices and special promotions that we offer as incentives."
              }
            ].map((item, idx) => (
              <Card key={idx} className="bg-white border-slate-200 card-hover">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Qualifications Section */}
      <section className="section-padding bg-white" data-testid="qualifications-section">
        <div className="container-max mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
                Team Requirements
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Our Marketers Know How to Get It Done
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                We hire only the best. Our OPC marketing team members possess the skills, 
                attitude, and dedication needed to deliver exceptional results for our resort partners.
              </p>
              <Link to="/careers">
                <Button 
                  className="bg-[#1E3A8A] hover:bg-[#1e40af]"
                  data-testid="opc-careers-cta"
                >
                  Join Our Marketing Team
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-6">Qualifications for Our Marketing Team</h3>
              <ul className="space-y-4">
                {qualifications.map((qual, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#EA580C] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">{qual}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="section-padding bg-[#0F172A]">
        <div className="container-max mx-auto text-center">
          <blockquote className="max-w-3xl mx-auto">
            <p className="text-2xl sm:text-3xl font-light text-white italic mb-6">
              "Marketing is no longer about the stuff that you make, but about 
              the stories you tell."
            </p>
            <cite className="text-slate-400 not-italic font-medium">— Seth Godin</cite>
          </blockquote>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="bg-gradient-to-r from-[#1E3A8A] to-[#0D9488] rounded-3xl p-8 lg:p-16 text-center">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
              Transform Your OPC Marketing Strategy Today
            </h2>
            <p className="text-white/80 mb-10 max-w-2xl mx-auto">
              Partner with us to drive more qualified tours to your resort. 
              Our experienced team is ready to deliver results.
            </p>
            <Link to="/partner">
              <Button 
                size="lg" 
                className="bg-[#EA580C] hover:bg-[#c2410c] text-white px-10"
                data-testid="opc-cta-partner"
              >
                Get Started Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TimeshareOPC;
